import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { 
  ChevronRight, Home, X, Hash, FileText, Building2, 
  Wallet, Tag, ShoppingCart, DollarSign, Calendar, 
  Clock, Timer, AlertCircle, Megaphone, CheckCircle, 
  Info, Eye, Bookmark, Share2, Printer,
  Users, Package, ArrowRight, Link as LinkIcon, CreditCard
} from "lucide-react";
import { Input } from "../components/ui/input";
import { Checkbox } from "../components/ui/checkbox";
import { useIsMobile } from "../components/ui/use-mobile";
import { RichTextContent } from "../components/RichTextContent";

interface AnnouncementDetail {
  id: number;
  project_no: string;
  project_name: string;
  bidding_unit: string;
  announcement_type: number;
  budget: number | null;
  project_nature: string | null;
  procurement_method: string | null;
  billing_method: string | null;
  document_price: number;
  open_bid_time: string | null;
  file_get_start_time: string | null;
  file_get_end_time: string | null;
  publish_time: string;
  content_pdf: string | null;
  announcement_url: string | null;
  packages: PackageInfo[];
  allow_joint_bid?: number;
  notice_type?: number;
  notice_content?: string | null;
  notice_url?: string | null;
  pubulish_version?: number | null;
  announcement_text?: string | null;
}

interface ChangeHistoryItem {
  id: number;
  project_no: string;
  project_name: string;
  publish_time: string;
  pubulish_version: number;
}

interface PackageInfo {
  id: number;
  package_name: string;
  package_content: string;
  package_url: string | null;
}

// 公告类型配置
const announcementTypeConfig: { [key: number]: { name: string; icon: any; color: string; bgColor: string } } = {
  1: { name: "招标公告", icon: Megaphone, color: "#3b82f6", bgColor: "#eff6ff" },
  2: { name: "变更公告", icon: AlertCircle, color: "#f59e0b", bgColor: "#fffbeb" },
  3: { name: "中标公告", icon: CheckCircle, color: "#10b981", bgColor: "#ecfdf5" },
  4: { name: "其他公告", icon: Info, color: "#8b5cf6", bgColor: "#f5f3ff" },
};

// 处理链接，确保有协议前缀
const formatUrl = (url: string | null): string | null => {
  if (!url) return null;
  // 如果已经有协议前缀，直接返回
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }
  // 否则添加 https:// 前缀
  return `https://${url}`;
};

// 信息项组件
const InfoItem = ({ icon: Icon, label, value, valueColor, highlight, isLink, isMobile = false }: { 
  icon: React.ElementType; 
  label: string; 
  value: string | null; 
  valueColor?: string;
  highlight?: boolean;
  isLink?: boolean;
  isMobile?: boolean;
}) => (
  <div style={{ 
    display: 'flex', 
    alignItems: 'flex-start', 
    gap: isMobile ? '12px' : '16px',
    padding: isMobile ? '12px 16px' : '16px 20px',
    backgroundColor: highlight ? '#fffbeb' : '#f8fafc',
    borderRadius: '12px',
    transition: 'all 0.2s'
  }}>
    <div style={{ 
      width: isMobile ? '32px' : '40px', 
      height: isMobile ? '32px' : '40px', 
      borderRadius: isMobile ? '8px' : '10px', 
      backgroundColor: 'white',
      boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0
    }}>
      <Icon style={{ width: isMobile ? '16px' : '20px', height: isMobile ? '16px' : '20px', color: '#3b82f6' }} />
    </div>
    <div style={{ flex: 1 }}>
      <div style={{ fontSize: isMobile ? '12px' : '13px', color: '#6b7280', marginBottom: '6px' }}>{label}</div>
      {isLink && value ? (
        <a 
          href={formatUrl(value) || '#'} 
          target="_blank" 
          rel="noopener noreferrer"
          style={{ 
            fontSize: isMobile ? '13px' : '15px', 
            fontWeight: 600, 
            color: '#3b82f6',
            textDecoration: 'none',
            wordBreak: 'break-all'
          }}
        >
          {value}
        </a>
      ) : (
        <div style={{ fontSize: isMobile ? '13px' : '15px', fontWeight: 600, color: valueColor || '#1f2937' }}>
          {value || "-"}
        </div>
      )}
    </div>
  </div>
);

// PDF预览组件
const PdfPreview = ({ url, title }: { url: string | null; title?: string }) => {
  if (!url) {
    return (
      <div style={{ 
        backgroundColor: '#f8fafc', 
        borderRadius: '12px', 
        height: '500px', 
        display: 'flex', 
        flexDirection: 'column',
        alignItems: 'center', 
        justifyContent: 'center',
        border: '2px dashed #e5e7eb'
      }}>
        <div style={{
          width: '80px',
          height: '80px',
          borderRadius: '20px',
          backgroundColor: 'white',
          boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginBottom: '20px'
        }}>
          <FileText style={{ width: '40px', height: '40px', color: '#9ca3af' }} />
        </div>
        <p style={{ color: '#6b7280', fontSize: '16px', marginBottom: '8px' }}>暂无PDF文档</p>
        <p style={{ color: '#9ca3af', fontSize: '14px' }}>请联系管理员上传公告文件</p>
      </div>
    );
  }

  return (
    <div style={{ borderRadius: '12px', overflow: 'hidden', border: '1px solid #e5e7eb' }}>
      {title && (
        <div style={{ 
          padding: '12px 16px', 
          backgroundColor: '#f8fafc', 
          borderBottom: '1px solid #e5e7eb',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <span style={{ fontSize: '14px', fontWeight: 500, color: '#374151' }}>{title}</span>
          {/* 公告详情页：不提供下载入口（隐藏“下载公告”按钮） */}
        </div>
      )}
      <iframe
        src={url}
        style={{
          width: '100%',
          height: '600px',
          border: 'none',
          backgroundColor: '#f8fafc'
        }}
        title={title || 'PDF预览'}
      />
    </div>
  );
};

export default function AnnouncementDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [detail, setDetail] = useState<AnnouncementDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [selectedPackages, setSelectedPackages] = useState<number[]>([]);
  const [isConsortium, setIsConsortium] = useState(false);
  const [consortiumMembers, setConsortiumMembers] = useState("");
  const [hoveredAction, setHoveredAction] = useState<string | null>(null);
  const [activePackageTab, setActivePackageTab] = useState<number>(0);
  const [hasRegistered, setHasRegistered] = useState(false);
  const [showChangeHistoryModal, setShowChangeHistoryModal] = useState(false);
  const [changeHistory, setChangeHistory] = useState<ChangeHistoryItem[]>([]);
  const [loadingChangeHistory, setLoadingChangeHistory] = useState(false);
  const [hasMultipleChanges, setHasMultipleChanges] = useState(false);
  const [otherAnnouncementFile, setOtherAnnouncementFile] = useState<File | null>(null);
  const [otherAnnouncementUploading, setOtherAnnouncementUploading] = useState(false);

  // 加载详情数据
  useEffect(() => {
    if (id) {
      fetchDetail(id);
      checkRegistrationStatus(id);
    }
  }, [id]);

  // 检查用户是否已报名
  const checkRegistrationStatus = async (announcementId: string) => {
    const token = localStorage.getItem("token");
    const userStr = localStorage.getItem("user");
    
    if (!token || !userStr) {
      setHasRegistered(false);
      return;
    }
    
    try {
      const user = JSON.parse(userStr);
      // 只有供应商才检查报名状态
      if (user.userType !== 'supplier') {
        setHasRegistered(false);
        return;
      }
      
      const resp = await fetch(`/api/trade-orders/check/${announcementId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await resp.json();
      
      if (data.ok) {
        setHasRegistered(data.registered);
      }
    } catch (err) {
      console.error('检查报名状态失败:', err);
    }
  };

  const fetchDetail = async (announcementId: string) => {
    setLoading(true);
    try {
      const resp = await fetch(`/api/trade-announcements/${announcementId}`);
      const data = await resp.json();
      if (data.ok) {
        setDetail(data.data);
        // 默认选中第一个分包tab
        if (data.data.packages && data.data.packages.length > 0) {
          setActivePackageTab(0);
        }
        // 如果是变更公告，检查是否有多个版本
        if (data.data.announcement_type === 2 && data.data.project_no) {
          checkMultipleChanges(data.data.project_no);
        }
      }
    } catch (err) {
      console.error('获取公告详情失败:', err);
    } finally {
      setLoading(false);
    }
  };

  // 检查是否有多个变更公告版本（pubulish_version >= 2）
  const checkMultipleChanges = async (projectNo: string) => {
    try {
      const resp = await fetch(`/api/trade-announcements/change-history/${projectNo}`);
      const data = await resp.json();
      if (data.ok && data.data && data.data.length > 0) {
        // 检查是否有版本号 >= 2 的记录
        const hasVersion2OrMore = data.data.some((item: ChangeHistoryItem) => item.pubulish_version >= 2);
        setHasMultipleChanges(hasVersion2OrMore);
      } else {
        setHasMultipleChanges(false);
      }
    } catch (err) {
      console.error('检查变更公告版本失败:', err);
      setHasMultipleChanges(false);
    }
  };

  // 获取历史变更公告列表
  const fetchChangeHistory = async (projectNo: string) => {
    setLoadingChangeHistory(true);
    setShowChangeHistoryModal(true);
    try {
      const resp = await fetch(`/api/trade-announcements/change-history/${projectNo}`);
      const data = await resp.json();
      if (data.ok) {
        setChangeHistory(data.data || []);
      } else {
        setChangeHistory([]);
      }
    } catch (err) {
      console.error('获取历史变更公告失败:', err);
      setChangeHistory([]);
    } finally {
      setLoadingChangeHistory(false);
    }
  };

  // 检查是否为供应商登录
  const checkSupplierAuth = () => {
    const userStr = localStorage.getItem("user");
    if (!userStr) return { isSupplier: false, isLoggedIn: false };
    try {
      const user = JSON.parse(userStr);
      // 只有供应商才能报名，userType为supplier
      const isSupplier = user && user.userType === 'supplier';
      return { isSupplier, isLoggedIn: true };
    } catch {
      return { isSupplier: false, isLoggedIn: false };
    }
  };

  // 获取报名按钮状态
  const getRegistrationStatus = () => {
    if (!detail) return null;

    const now = new Date();
    const startTime = detail.file_get_start_time ? new Date(detail.file_get_start_time) : null;
    const endTime = detail.file_get_end_time ? new Date(detail.file_get_end_time) : null;

    if (startTime && now < startTime) {
      return "not_started";
    }
    if (endTime && now > endTime) {
      return "ended";
    }
    return "active";
  };

  // 处理报名点击
  const handleRegisterClick = () => {
    const authStatus = checkSupplierAuth();
    if (!authStatus.isSupplier) {
      setShowLoginModal(true);
      return;
    }
    setShowRegisterModal(true);
  };

  // 处理登录跳转
  const handleGoToLogin = () => {
    // 清空当前登录状态
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    setShowLoginModal(false);
    // 跳转到供应商登录页面
    navigate('/my-bids');
  };

  // 处理分包选择
  const handlePackageSelect = (packageId: number) => {
    setSelectedPackages((prev) =>
      prev.includes(packageId)
        ? prev.filter((id) => id !== packageId)
        : [...prev, packageId]
    );
  };

  // 全选/取消全选
  const handleSelectAll = () => {
    if (!detail) return;
    if (selectedPackages.length === detail.packages.length) {
      setSelectedPackages([]);
    } else {
      setSelectedPackages(detail.packages.map((p) => p.id));
    }
  };

  // 计算应付金额
  const calculateAmount = () => {
    if (!detail) return 0;
    // 按项目收费：应付金额 = document_price
    // 按包收费：应付金额 = 选择的包数量 * document_price
    if (detail.billing_method === '按包收费') {
      return selectedPackages.length * (detail.document_price || 0);
    }
    return detail.document_price || 0;
  };

  // 处理立即购买
  const handlePurchase = async () => {
    if (!detail || selectedPackages.length === 0) {
      alert("请至少选择一个包件");
      return;
    }
    
    // 获取当前用户信息
    const userStr = localStorage.getItem("user");
    if (!userStr) {
      alert("请先登录");
      return;
    }
    
    const user = JSON.parse(userStr);
    const token = localStorage.getItem("token");
    const orderAmount = calculateAmount();
    
    try {
      // 生成订单
      const resp = await fetch('/api/trade-orders', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          announcement_id: detail.id,
          package_ids: selectedPackages,
          order_amount: orderAmount,
          project_name: detail.project_name,
          project_no: detail.project_no,
          project_nature: detail.project_nature,
          supplier_account: user.account,
          supplier_name: user.company_name,
          supplier_manager_name: user.username,
          supplier_manager_phone: user.phone,
          bank_name: user.bank_name,
          bank_account: user.bank_account,
          is_consortium: isConsortium,
          consortium_members: consortiumMembers
        })
      });
      
      const data = await resp.json();
      
      if (data.ok) {
        // 订单创建成功，跳转到支付页面
        navigate(`/payment?orderId=${data.order_id}`);
      } else {
        alert(data.error || '订单创建失败');
      }
    } catch (err) {
      console.error('创建订单失败:', err);
      alert('创建订单失败，请稍后重试');
    }
  };

  // 处理其他公告报名提交
  const handleOtherAnnouncementSubmit = async () => {
    if (!detail || !otherAnnouncementFile) {
      alert('请上传报名文件');
      return;
    }

    const token = localStorage.getItem("token");
    if (!token) {
      setShowLoginModal(true);
      return;
    }

    setOtherAnnouncementUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', otherAnnouncementFile);
      formData.append('announcement_id', detail.id.toString());
      formData.append('project_name', detail.project_name);
      formData.append('project_no', detail.project_no);

      const resp = await fetch('/api/trade-orders/other', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      const data = await resp.json();

      if (data.ok) {
        alert('报名成功');
        setShowRegisterModal(false);
        setOtherAnnouncementFile(null);
        // 重新检查报名状态
        await checkRegistrationStatus(detail.id.toString());
      } else {
        alert(data.error || '报名失败');
      }
    } catch (err) {
      console.error('报名失败:', err);
      alert('报名失败，请稍后重试');
    } finally {
      setOtherAnnouncementUploading(false);
    }
  };

  // 格式化金额
  const formatMoney = (amount: number | null) => {
    if (amount === null || amount === undefined) return "-";
    return new Intl.NumberFormat("zh-CN", {
      style: "currency",
      currency: "CNY",
      minimumFractionDigits: 2,
    }).format(amount);
  };

  // 格式化日期时间
  const formatDateTime = (dateStr: string | null) => {
    if (!dateStr) return "-";
    const date = new Date(dateStr);
    return date.toLocaleString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    }).replace(/\//g, '-');
  };

  // 格式化日期（只显示年月日）
  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return "-";
    const date = new Date(dateStr);
    return date.toLocaleDateString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    }).replace(/\//g, '-');
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#f8fafc' }}>
        <Header />
        <div style={{ textAlign: 'center', padding: '100px 0' }}>
          <div style={{ 
            width: '80px', 
            height: '80px', 
            margin: '0 auto 24px', 
            backgroundColor: '#f3f4f6', 
            borderRadius: '50%', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center' 
          }}>
            <FileText style={{ width: '32px', height: '32px', color: '#9ca3af' }} />
          </div>
          <p style={{ color: '#6b7280', fontSize: '16px' }}>加载中...</p>
        </div>
      </div>
    );
  }

  if (!detail) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#f8fafc' }}>
        <Header />
        <div style={{ textAlign: 'center', padding: '100px 0' }}>
          <div style={{ 
            width: '80px', 
            height: '80px', 
            margin: '0 auto 24px', 
            backgroundColor: '#f3f4f6', 
            borderRadius: '50%', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center' 
          }}>
            <FileText style={{ width: '32px', height: '32px', color: '#9ca3af' }} />
          </div>
          <p style={{ color: '#6b7280', fontSize: '16px', marginBottom: '16px' }}>公告不存在</p>
          <Link to="/trade-announcements" style={{ color: '#3b82f6', textDecoration: 'none' }}>
            返回公告列表
          </Link>
        </div>
      </div>
    );
  }

  const typeConfig = announcementTypeConfig[detail.announcement_type] || announcementTypeConfig[1];
  const TypeIcon = typeConfig.icon;
  const registrationStatus = getRegistrationStatus();

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f8fafc' }}>
      <Header />

      {/* 页面横幅 */}
      <div style={{
        background: `linear-gradient(135deg, ${typeConfig.color} 0%, ${typeConfig.color}cc 50%, ${typeConfig.color}99 100%)`,
        padding: isMobile ? '32px 0 40px' : '48px 0 60px',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* 装饰元素 */}
        <div style={{
          position: 'absolute',
          top: '-60px',
          right: '-40px',
          width: '350px',
          height: '350px',
          borderRadius: '50%',
          background: 'rgba(255,255,255,0.08)',
        }} />
        <div style={{
          position: 'absolute',
          bottom: '-80px',
          left: '10%',
          width: '200px',
          height: '200px',
          borderRadius: '50%',
          background: 'rgba(255,255,255,0.05)',
        }} />
        
        <div style={{ width: isMobile ? '90%' : '80%', margin: '0 auto', position: 'relative', zIndex: 1 }}>
          <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'flex-start' : 'center', gap: isMobile ? '8px' : '12px', marginBottom: isMobile ? '16px' : '20px', flexWrap: 'wrap' }}>
            <span style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              padding: isMobile ? '6px 12px' : '8px 16px',
              backgroundColor: 'rgba(255,255,255,0.2)',
              borderRadius: '24px',
              fontSize: isMobile ? '12px' : '14px',
              color: 'white',
              fontWeight: 500
            }}>
              <TypeIcon style={{ width: isMobile ? '14px' : '16px', height: isMobile ? '14px' : '16px' }} />
              {typeConfig.name}
            </span>
            <span style={{
              padding: isMobile ? '4px 10px' : '6px 12px',
              backgroundColor: 'rgba(255,255,255,0.15)',
              borderRadius: '20px',
              fontSize: isMobile ? '11px' : '13px',
              color: 'rgba(255,255,255,0.9)'
            }}>
              {detail.project_no}
            </span>
          </div>
          <h1 style={{ 
            fontSize: isMobile ? '20px' : '28px', 
            fontWeight: 700, 
            color: 'white',
            lineHeight: 1.4,
            maxWidth: '900px',
            textShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}>
            {detail.project_name}
          </h1>
          <div style={{ 
            display: 'flex', 
            flexDirection: isMobile ? 'column' : 'row',
            alignItems: isMobile ? 'flex-start' : 'center', 
            gap: isMobile ? '8px' : '24px', 
            marginTop: isMobile ? '16px' : '20px',
            fontSize: isMobile ? '12px' : '14px',
            color: 'rgba(255,255,255,0.85)'
          }}>
            {detail.announcement_type === 1 && detail.bidding_unit && (
              <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Building2 style={{ width: isMobile ? '14px' : '16px', height: isMobile ? '14px' : '16px' }} />
                {detail.bidding_unit}
              </span>
            )}
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Calendar style={{ width: isMobile ? '14px' : '16px', height: isMobile ? '14px' : '16px' }} />
              发布时间：{formatDate(detail.publish_time)}
            </span>
          </div>
        </div>
      </div>

      {/* 面包屑导航 */}
      <div style={{ backgroundColor: 'white', borderBottom: '1px solid #e5e7eb', boxShadow: '0 1px 2px rgba(0,0,0,0.03)' }}>
        <div style={{ width: isMobile ? '90%' : '80%', margin: '0 auto', padding: isMobile ? '12px 0' : '16px 0' }}>
          <nav style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '6px' : '8px', fontSize: isMobile ? '12px' : '14px', flexWrap: 'wrap' }}>
            <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#6b7280', textDecoration: 'none' }}>
              <Home style={{ width: isMobile ? '14px' : '16px', height: isMobile ? '14px' : '16px' }} />
              首页
            </Link>
            <ChevronRight style={{ width: isMobile ? '14px' : '16px', height: isMobile ? '14px' : '16px', color: '#d1d5db' }} />
            <Link to="/trade-announcements" style={{ color: '#6b7280', textDecoration: 'none' }}>
              交易公告
            </Link>
            <ChevronRight style={{ width: isMobile ? '14px' : '16px', height: isMobile ? '14px' : '16px', color: '#d1d5db' }} />
            <span style={{ color: typeConfig.color, fontWeight: 500 }}>{typeConfig.name}</span>
          </nav>
        </div>
      </div>

      {/* 主内容区域 */}
      <div style={{ width: isMobile ? '90%' : '80%', margin: '0 auto', padding: isMobile ? '20px 0' : '40px 0' }}>
        <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? '20px' : '32px' }}>
          {/* 左侧内容 */}
          <div style={{ flex: 1, order: isMobile ? 1 : 0 }}>
            {/* 项目信息卡片 */}
            <div style={{
              backgroundColor: 'white',
              borderRadius: '16px',
              boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
              overflow: 'hidden',
              marginBottom: '24px'
            }}>
              <div style={{
                padding: isMobile ? '16px' : '24px 32px',
                borderBottom: '1px solid #f3f4f6',
                background: 'linear-gradient(to right, #fafbfc, white)'
              }}>
                <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'flex-start' : 'center', justifyContent: 'space-between', gap: isMobile ? '12px' : '0' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div style={{
                      width: '4px',
                      height: isMobile ? '20px' : '24px',
                      backgroundColor: typeConfig.color,
                      borderRadius: '2px'
                    }} />
                    <h3 style={{ fontSize: isMobile ? '16px' : '18px', fontWeight: 600, color: '#1f2937' }}>项目信息</h3>
                  </div>
                  {/* 变更公告：如果同一项目有多个变更公告（pubulish_version >= 2），显示提示 */}
                  {detail.announcement_type === 2 && hasMultipleChanges && (
                    <div style={{ fontSize: isMobile ? '12px' : '14px', color: '#6b7280', lineHeight: 1.5 }}>
                      此项目存在多个变更公告，如需查看历史信息，请
                      <span
                        onClick={() => fetchChangeHistory(detail.project_no)}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.color = '#d46b08';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.color = '#f59e0b';
                        }}
                        style={{
                          color: '#f59e0b',
                          cursor: 'pointer',
                          marginLeft: '4px',
                          textDecoration: 'underline'
                        }}
                      >
                        前往查看
                      </span>
                    </div>
                  )}
                </div>
              </div>
              
              <div style={{ padding: isMobile ? '16px' : '24px 32px' }}>
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: isMobile ? 'repeat(1, 1fr)' : 'repeat(2, 1fr)', 
                  gap: isMobile ? '12px' : '16px'
                }}>
                  {/* 招标公告显示的字段 */}
                  {detail.announcement_type === 1 && (
                    <>
                      <InfoItem icon={Hash} label="项目编号" value={detail.project_no} isMobile={isMobile} />
                      <InfoItem icon={FileText} label="项目名称" value={detail.project_name} isMobile={isMobile} />
                      <InfoItem icon={LinkIcon} label="公告链接" value={detail.announcement_url} isLink isMobile={isMobile} />
                      <InfoItem icon={ShoppingCart} label="采购方式" value={detail.procurement_method} isMobile={isMobile} />
                      <InfoItem icon={Clock} label="开标时间" value={formatDateTime(detail.open_bid_time)} isMobile={isMobile} />
                    </>
                  )}
                  
                  {/* 变更/中标/其他公告显示的字段 */}
                  {detail.announcement_type !== 1 && (
                    <>
                      <InfoItem icon={Hash} label="项目编号" value={detail.project_no} isMobile={isMobile} />
                      <InfoItem icon={FileText} label="项目名称" value={detail.project_name} isMobile={isMobile} />
                      <div style={{ gridColumn: isMobile ? 'span 1' : 'span 2' }}>
                        <InfoItem icon={LinkIcon} label="公告链接" value={detail.announcement_url} isLink isMobile={isMobile} />
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* 公告内容卡片 */}
            <div style={{
              backgroundColor: 'white',
              borderRadius: '16px',
              boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
              overflow: 'hidden'
            }}>
              <div style={{
                padding: isMobile ? '16px' : '24px 32px',
                borderBottom: '1px solid #f3f4f6',
                background: 'linear-gradient(to right, #fafbfc, white)'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div style={{
                      width: '4px',
                      height: isMobile ? '20px' : '24px',
                      backgroundColor: typeConfig.color,
                      borderRadius: '2px'
                    }} />
                    <h3 style={{ fontSize: isMobile ? '16px' : '18px', fontWeight: 600, color: '#1f2937' }}>公告内容</h3>
                  </div>
                </div>
              </div>
              
              <div style={{ padding: isMobile ? '16px' : '32px' }}>
                {/* 招标公告 - 分包选项卡 + PDF预览 */}
                {detail.announcement_type === 1 && detail.packages && detail.packages.length > 0 ? (
                  <div>
                    {/* 分包选项卡 */}
                    <div style={{ 
                      display: 'flex', 
                      gap: isMobile ? '6px' : '8px', 
                      marginBottom: isMobile ? '16px' : '24px',
                      borderBottom: '2px solid #e5e7eb',
                      paddingBottom: '0',
                      overflowX: isMobile ? 'auto' : 'visible',
                      WebkitOverflowScrolling: 'touch'
                    }}>
                      {detail.packages.map((pkg, index) => (
                        <button
                          key={pkg.id}
                          onClick={() => setActivePackageTab(index)}
                          style={{
                            padding: isMobile ? '10px 16px' : '12px 24px',
                            backgroundColor: activePackageTab === index ? 'white' : 'transparent',
                            color: activePackageTab === index ? typeConfig.color : '#6b7280',
                            border: 'none',
                            borderBottom: activePackageTab === index ? `3px solid ${typeConfig.color}` : '3px solid transparent',
                            marginBottom: '-2px',
                            cursor: 'pointer',
                            fontSize: isMobile ? '12px' : '14px',
                            fontWeight: activePackageTab === index ? 600 : 500,
                            transition: 'all 0.2s',
                            whiteSpace: 'nowrap',
                            flexShrink: 0
                          }}
                        >
                          {pkg.package_name}
                        </button>
                      ))}
                    </div>
                    
                    {/* 当前分包内容 */}
                    {detail.packages[activePackageTab] && (
                      <div>
                        <div style={{
                          padding: '20px',
                          backgroundColor: '#f8fafc',
                          borderRadius: '12px',
                          marginBottom: '24px'
                        }}>
                          <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '8px' }}>包件内容</div>
                          <div style={{ fontSize: '15px', color: '#1f2937', lineHeight: 1.8 }}>
                            {detail.packages[activePackageTab].package_content || '暂无包件内容说明'}
                          </div>
                        </div>
                        
                        {/* 招标公告相关内容 */}
                        {detail.notice_type === 0 && detail.notice_content && (
                          <div style={{
                            padding: isMobile ? '24px' : '40px',
                            backgroundColor: '#fefefe',
                            borderRadius: '16px',
                            marginBottom: '24px',
                            border: '1px solid #e8e8e8',
                            boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
                            position: 'relative'
                          }}>
                            <div style={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              right: 0,
                              height: '4px',
                              background: 'linear-gradient(90deg, #b8860b, #daa520, #b8860b)',
                              borderRadius: '16px 16px 0 0'
                            }} />
                            <RichTextContent
                              html={detail.notice_content}
                              style={{ fontSize: "16px", color: "#2d3748", lineHeight: 2.2, textAlign: 'justify', wordBreak: 'break-word' }}
                            />
                          </div>
                        )}
                        {detail.notice_type === 1 && detail.notice_url && (
                          <div style={{ marginBottom: '24px' }}>
                            <PdfPreview 
                              url={detail.notice_url} 
                              title="招标采购公告"
                            />
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ) : detail.announcement_type === 1 ? (
                  // 招标公告但没有分包
                  <PdfPreview url={detail.content_pdf} title="招标公告文件" />
                ) : detail.announcement_type === 2 ? (
                  // 变更公告
                  detail.notice_url ? (
                    <PdfPreview url={detail.notice_url} title="公告文件" />
                  ) : detail.notice_content ? (
                    <div style={{
                      padding: isMobile ? '24px' : '40px',
                      backgroundColor: '#fefefe',
                      borderRadius: '16px',
                      border: '1px solid #e8e8e8',
                      boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
                      position: 'relative'
                    }}>
                      <div style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: '4px',
                        background: 'linear-gradient(90deg, #b8860b, #daa520, #b8860b)',
                        borderRadius: '16px 16px 0 0'
                      }} />
                      <RichTextContent
                        html={detail.notice_content}
                        style={{ fontSize: "15px", color: "#2d3748", lineHeight: 2.2, textAlign: 'justify' }}
                      />
                    </div>
                  ) : (
                    <div style={{
                      backgroundColor: '#f8fafc',
                      borderRadius: '12px',
                      height: '500px',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: '2px dashed #e5e7eb'
                    }}>
                      <div style={{
                        width: '80px',
                        height: '80px',
                        borderRadius: '20px',
                        backgroundColor: 'white',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginBottom: '20px'
                      }}>
                        <FileText style={{ width: '40px', height: '40px', color: '#9ca3af' }} />
                      </div>
                      <p style={{ color: '#6b7280', fontSize: '16px', marginBottom: '8px' }}>暂无变更公告内容</p>
                    </div>
                  )
                ) : (
                  // 中标公告或其他公告 - 显示PDF或announcement_text文本
                  detail.announcement_text ? (
                    <div style={{
                      padding: isMobile ? '24px' : '40px',
                      backgroundColor: '#fefefe',
                      borderRadius: '12px',
                      border: '1px solid #e8e8e8',
                      boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
                      position: 'relative'
                    }}>
                      <div style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: '4px',
                        background: 'linear-gradient(90deg, #b8860b, #daa520, #b8860b)',
                        borderRadius: '16px 16px 0 0'
                      }} />
                      <RichTextContent
                        html={detail.announcement_text}
                        style={{ 
                          fontSize: "16px", 
                          color: "#2d3748", 
                          lineHeight: 2.2,
                          textAlign: 'justify',
                         whiteSpace: 'pre-wrap'
                        }}
                      />
                    </div>
                  ) : detail.content_pdf ? (
                    <PdfPreview url={detail.content_pdf} title="公告文件" />
                  ) : (
                    <div style={{
                      backgroundColor: '#f8fafc',
                      borderRadius: '12px',
                      height: '500px',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: '2px dashed #e5e7eb'
                    }}>
                      <div style={{
                        width: '80px',
                        height: '80px',
                        borderRadius: '20px',
                        backgroundColor: 'white',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginBottom: '20px'
                      }}>
                        <FileText style={{ width: '40px', height: '40px', color: '#9ca3af' }} />
                      </div>
                      <p style={{ color: '#6b7280', fontSize: '16px', marginBottom: '8px' }}>暂无公告内容</p>
                    </div>
                  )
                )}

                {/* 已报名供应商：下载附件 */}
                {hasRegistered && (detail.content_pdf || detail.notice_url) && (
                  <div style={{
                    marginTop: '24px',
                    padding: '16px 20px',
                    backgroundColor: '#eff6ff',
                    borderRadius: '12px',
                    border: '1px solid #bfdbfe',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px'
                  }}>
                    <div style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '10px',
                      backgroundColor: '#3b82f6',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0
                    }}>
                      <FileText style={{ width: '20px', height: '20px', color: 'white' }} />
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: '14px', fontWeight: 600, color: '#1e40af', marginBottom: '4px' }}>
                        下载附件
                      </div>
                      <div style={{ fontSize: '13px', color: '#3b82f6' }}>
                        <a
                          href={detail.content_pdf || detail.notice_url || '#'}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            color: '#2563eb',
                            textDecoration: 'none',
                            fontWeight: 500,
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px'
                          }}
                        >
                          点击下载公告附件
                          <span style={{ fontSize: '12px' }}>↓</span>
                        </a>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* 右侧边栏 */}
          <div style={{ width: isMobile ? '100%' : '320px', flexShrink: 0, order: isMobile ? 0 : 1 }}>
            {/* 报名卡片 - 招标公告和其他公告显示 */}
            {(detail.announcement_type === 1 || detail.announcement_type === 4) && (
              <div style={{
                backgroundColor: 'white',
                borderRadius: '16px',
                boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                overflow: 'hidden',
                marginBottom: '24px',
                position: 'sticky',
                top: '24px'
              }}>
                <div style={{
                  padding: isMobile ? '20px' : '24px',
                  background: `linear-gradient(135deg, ${typeConfig.color}, ${typeConfig.color}cc)`,
                  color: 'white',
                  textAlign: 'center'
                }}>
                  <div style={{
                    width: isMobile ? '48px' : '64px',
                    height: isMobile ? '48px' : '64px',
                    borderRadius: '50%',
                    backgroundColor: 'rgba(255,255,255,0.2)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 16px'
                  }}>
                    <Users style={{ width: isMobile ? '24px' : '32px', height: isMobile ? '24px' : '32px' }} />
                  </div>
                  <h4 style={{ fontSize: isMobile ? '16px' : '18px', fontWeight: 600, marginBottom: '8px' }}>项目报名</h4>
                </div>
                
                <div style={{ padding: isMobile ? '20px' : '24px' }}>
                  {/* 报名时间提示 */}
                  {detail.file_get_start_time && detail.file_get_end_time && (
                    <div style={{
                      padding: isMobile ? '12px' : '16px',
                      backgroundColor: '#f8fafc',
                      borderRadius: '12px',
                      marginBottom: isMobile ? '16px' : '20px',
                      fontSize: isMobile ? '12px' : '13px',
                      color: '#6b7280'
                    }}>
                      <div style={{ marginBottom: '8px' }}>
                        <span style={{ color: '#9ca3af' }}>报名开始：</span>
                        <span style={{ color: '#374151' }}>{formatDateTime(detail.file_get_start_time)}</span>
                      </div>
                      <div>
                        <span style={{ color: '#9ca3af' }}>报名截止：</span>
                        <span style={{ color: '#374151' }}>{formatDateTime(detail.file_get_end_time)}</span>
                      </div>
                    </div>
                  )}
                  
                  {/* 报名按钮 */}
                  {registrationStatus === "not_started" ? (
                    <button
                      disabled
                      style={{
                        width: '100%',
                        padding: '16px',
                        backgroundColor: '#e5e7eb',
                        color: '#9ca3af',
                        fontSize: '16px',
                        fontWeight: 600,
                        border: 'none',
                        borderRadius: '12px',
                        cursor: 'not-allowed'
                      }}
                    >
                      报名未开始
                    </button>
                  ) : registrationStatus === "ended" ? (
                    <button
                      disabled
                      style={{
                        width: '100%',
                        padding: '16px',
                        backgroundColor: '#e5e7eb',
                        color: '#9ca3af',
                        fontSize: '16px',
                        fontWeight: 600,
                        border: 'none',
                        borderRadius: '12px',
                        cursor: 'not-allowed'
                      }}
                    >
                      报名已截止
                    </button>
                  ) : (detail.announcement_type === 1 || detail.announcement_type === 4) && hasRegistered ? (
                    <button
                      onClick={() => navigate('//my-bids')}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = '0 8px 25px rgba(16, 185, 129, 0.4)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = '0 4px 15px rgba(16, 185, 129, 0.3)';
                      }}
                      style={{
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px',
                        padding: '16px',
                        backgroundColor: '#10b981',
                        color: 'white',
                        fontSize: '16px',
                        fontWeight: 600,
                        border: 'none',
                        borderRadius: '12px',
                        cursor: 'pointer',
                        boxShadow: '0 4px 15px rgba(16, 185, 129, 0.3)',
                        transition: 'all 0.3s ease'
                      }}
                    >
                      <CheckCircle style={{ width: '20px', height: '20px' }} />
                      我已报名
                    </button>
                  ) : (
                    <button
                      onClick={handleRegisterClick}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = `0 8px 25px ${typeConfig.color}50`;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = `0 4px 15px ${typeConfig.color}40`;
                      }}
                      style={{
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px',
                        padding: '16px',
                        backgroundColor: typeConfig.color,
                        color: 'white',
                        fontSize: '16px',
                        fontWeight: 600,
                        border: 'none',
                        borderRadius: '12px',
                        cursor: 'pointer',
                        boxShadow: `0 4px 15px ${typeConfig.color}40`,
                        transition: 'all 0.3s ease'
                      }}
                    >
                      我要报名
                      <ArrowRight style={{ width: '20px', height: '20px' }} />
                    </button>
                  )}
                </div>
              </div>
            )}

            
          </div>
        </div>
      </div>

      {/* 报名弹窗 */}
      {showRegisterModal && detail && (
        <>
          <div
            style={{
              position: 'fixed',
              inset: 0,
              backgroundColor: 'rgba(0,0,0,0.6)',
              backdropFilter: 'blur(4px)',
              zIndex: 50
            }}
            onClick={() => setShowRegisterModal(false)}
          />
          <div style={{
            position: 'fixed',
            inset: 0,
            display: 'flex',
            alignItems: isMobile ? 'flex-end' : 'center',
            justifyContent: 'center',
            zIndex: 50,
            padding: isMobile ? '0' : '16px',
            overflow: isMobile ? 'hidden' : 'auto'
          }}>
            <div
              style={{
                backgroundColor: 'white',
                borderRadius: isMobile ? '20px 20px 0 0' : '20px',
                boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
                width: isMobile ? '100%' : '70vw',
                maxWidth: isMobile ? '100%' : '1000px',
                height: isMobile ? '92vh' : undefined,
                margin: isMobile ? '0' : '32px 0',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* 弹窗头部 */}
              <div style={{
                padding: isMobile ? '16px' : '24px 32px',
                background: `linear-gradient(135deg, ${typeConfig.color}, ${typeConfig.color}cc)`,
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '12px' : '16px' }}>
                  <div style={{
                    width: isMobile ? '40px' : '48px',
                    height: isMobile ? '40px' : '48px',
                    borderRadius: isMobile ? '10px' : '12px',
                    backgroundColor: 'rgba(255,255,255,0.2)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <Package style={{ width: isMobile ? '20px' : '24px', height: isMobile ? '20px' : '24px' }} />
                  </div>
                  <div>
                    <h2 style={{ fontSize: isMobile ? '18px' : '20px', fontWeight: 700, marginBottom: '4px' }}>项目报名</h2>
                    <p style={{ fontSize: isMobile ? '12px' : '14px', opacity: 0.9 }}>请选择需要购买的包件</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowRegisterModal(false)}
                  style={{
                    width: isMobile ? '36px' : '40px',
                    height: isMobile ? '36px' : '40px',
                    backgroundColor: 'rgba(255,255,255,0.15)',
                    border: 'none',
                    borderRadius: '10px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'background-color 0.2s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.25)'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.15)'}
                >
                  <X style={{ width: '20px', height: '20px', color: 'white' }} />
                </button>
              </div>

              {/* 弹窗内容 */}
              <div style={{ padding: isMobile ? '16px' : '32px', maxHeight: isMobile ? 'calc(92vh - 220px)' : 'calc(100vh - 280px)', overflow: 'auto' }}>
                {/* 其他公告：文件上传 */}
                {detail.announcement_type === 4 ? (
                  <>
                    {/* 项目信息 */}
                    <div style={{ marginBottom: '32px' }}>
                      <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '12px',
                        marginBottom: '20px'
                      }}>
                        <div style={{
                          width: '4px',
                          height: '20px',
                          backgroundColor: typeConfig.color,
                          borderRadius: '2px'
                        }} />
                        <h3 style={{ fontSize: '16px', fontWeight: 600, color: '#1f2937' }}>项目信息</h3>
                      </div>
                      
                      {/* 项目名称 */}
                      <div style={{ 
                        backgroundColor: typeConfig.bgColor, 
                        padding: '16px 20px', 
                        borderRadius: '12px',
                        marginBottom: '20px',
                        borderLeft: `4px solid ${typeConfig.color}`
                      }}>
                        <span style={{ color: typeConfig.color, fontWeight: 600, fontSize: '16px' }}>{detail.project_name}</span>
                      </div>
                      
                      {/* 两列信息 */}
                      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(1, 1fr)' : 'repeat(2, 1fr)', gap: '16px' }}>
                        <InfoItem icon={Hash} label="项目编号" value={detail.project_no} isMobile={isMobile} />
                      </div>
                    </div>

                    {/* 文件上传区域 */}
                    <div style={{ marginBottom: '32px' }}>
                      <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '12px',
                        marginBottom: '20px'
                      }}>
                        <div style={{
                          width: '4px',
                          height: '20px',
                          backgroundColor: typeConfig.color,
                          borderRadius: '2px'
                        }} />
                        <h3 style={{ fontSize: '16px', fontWeight: 600, color: '#1f2937' }}>上传报名文件</h3>
                      </div>
                      
                      <div style={{
                        border: '2px dashed #d1d5db',
                        borderRadius: '12px',
                        padding: '40px',
                        textAlign: 'center',
                        backgroundColor: '#f8fafc',
                        cursor: 'pointer',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.borderColor = typeConfig.color;
                        e.currentTarget.style.backgroundColor = typeConfig.bgColor;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = '#d1d5db';
                        e.currentTarget.style.backgroundColor = '#f8fafc';
                      }}
                      onClick={() => {
                        const input = document.createElement('input');
                        input.type = 'file';
                        input.accept = '*';
                        input.onchange = (e) => {
                          const file = (e.target as HTMLInputElement).files?.[0];
                          if (file) {
                            setOtherAnnouncementFile(file);
                          }
                        };
                        input.click();
                      }}
                      >
                        {otherAnnouncementFile ? (
                          <div>
                            <FileText style={{ width: '48px', height: '48px', color: '#10b981', margin: '0 auto 16px' }} />
                            <p style={{ color: '#10b981', fontSize: '16px', fontWeight: 600, marginBottom: '8px' }}>
                              ✓ 已选择文件
                            </p>
                            <p style={{ color: '#6b7280', fontSize: '14px', marginBottom: '8px' }}>
                              {otherAnnouncementFile.name}
                            </p>
                            <p style={{ color: '#3b82f6', fontSize: '14px', marginTop: '8px' }}>
                              点击重新选择
                            </p>
                          </div>
                        ) : (
                          <div>
                            <FileText style={{ width: '48px', height: '48px', color: typeConfig.color, margin: '0 auto 16px' }} />
                            <p style={{ color: '#374151', fontSize: '16px', fontWeight: 600, marginBottom: '8px' }}>
                              点击或拖拽文件到此区域上传
                            </p>
                            <p style={{ color: '#6b7280', fontSize: '14px' }}>
                              支持各种格式文件
                            </p>
                          </div>
                        )}
                      </div>
                      
                      <p style={{ 
                        fontSize: '14px', 
                        color: '#ff4d4f', 
                        marginTop: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}>
                        <AlertCircle style={{ width: '16px', height: '16px' }} />
                        建议上传50M以下文件，可以是压缩包文件，也可以是PDF
                      </p>
                    </div>
                  </>
                ) : (
                  <>
                    {/* 项目信息 */}
                    <div style={{ marginBottom: '32px' }}>
                      <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '12px',
                        marginBottom: '20px'
                      }}>
                        <div style={{
                          width: '4px',
                          height: '20px',
                          backgroundColor: typeConfig.color,
                          borderRadius: '2px'
                        }} />
                        <h3 style={{ fontSize: '16px', fontWeight: 600, color: '#1f2937' }}>项目信息</h3>
                      </div>
                  
                  {/* 项目名称 */}
                  <div style={{ 
                    backgroundColor: typeConfig.bgColor, 
                    padding: '16px 20px', 
                    borderRadius: '12px',
                    marginBottom: '20px',
                    borderLeft: `4px solid ${typeConfig.color}`
                  }}>
                    <span style={{ color: typeConfig.color, fontWeight: 600, fontSize: '16px' }}>{detail.project_name}</span>
                  </div>
                  
                  {/* 两列信息 */}
                  <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(1, 1fr)' : 'repeat(2, 1fr)', gap: '16px' }}>
                    <InfoItem icon={Hash} label="项目编号" value={detail.project_no} isMobile={isMobile} />
                    <InfoItem icon={Tag} label="项目性质" value={detail.project_nature} isMobile={isMobile} />
                    <InfoItem icon={ShoppingCart} label="采购方式" value={detail.procurement_method} isMobile={isMobile} />
                    <InfoItem icon={Clock} label="开标时间" value={formatDateTime(detail.open_bid_time)} isMobile={isMobile} />
                    <InfoItem icon={Timer} label="获取文件开始时间" value={formatDateTime(detail.file_get_start_time)} isMobile={isMobile} />
                    <InfoItem icon={AlertCircle} label="获取文件结束时间" value={formatDateTime(detail.file_get_end_time)} isMobile={isMobile} />
                  </div>
                </div>

                {/* 分包信息 */}
                {detail.packages && detail.packages.length > 0 && (
                  <div style={{ marginBottom: '32px' }}>
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '12px',
                      marginBottom: '20px'
                    }}>
                      <div style={{
                        width: '4px',
                        height: '20px',
                        backgroundColor: typeConfig.color,
                        borderRadius: '2px'
                      }} />
                      <h3 style={{ fontSize: '16px', fontWeight: 600, color: '#1f2937' }}>分包信息</h3>
                      <span style={{
                        padding: '4px 12px',
                        backgroundColor: typeConfig.bgColor,
                        color: typeConfig.color,
                        fontSize: '12px',
                        fontWeight: 500,
                        borderRadius: '20px'
                      }}>
                        共 {detail.packages.length} 个包件
                      </span>
                    </div>
                    
                    <div style={{ 
                      border: '1px solid #e5e7eb', 
                      borderRadius: '12px', 
                      overflow: 'hidden'
                    }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                          <tr style={{ backgroundColor: 'white' }}>
                            <th style={{ padding: '16px 20px', textAlign: 'center', width: '60px', verticalAlign: 'middle' }}>
                              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <Checkbox
                                  checked={selectedPackages.length === detail.packages.length && detail.packages.length > 0}
                                  onCheckedChange={handleSelectAll}
                                  className="bg-white border-gray-300"
                                />
                              </div>
                            </th>
                            <th style={{ padding: '16px 20px', textAlign: 'left', fontSize: '14px', fontWeight: 600, color: '#374151' }}>
                              包件名称
                            </th>
                            <th style={{ padding: '16px 20px', textAlign: 'left', fontSize: '14px', fontWeight: 600, color: '#374151' }}>
                              包件内容
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {detail.packages.map((pkg) => (
                            <tr
                              key={pkg.id}
                              onClick={() => handlePackageSelect(pkg.id)}
                              style={{
                                borderTop: '1px solid #e5e7eb',
                                backgroundColor: selectedPackages.includes(pkg.id) ? typeConfig.bgColor : 'white',
                                cursor: 'pointer',
                                transition: 'background-color 0.2s'
                              }}
                            >
                              <td style={{ padding: '16px 20px', textAlign: 'center', verticalAlign: 'middle' }}>
                                <div 
                                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <Checkbox
                                    checked={selectedPackages.includes(pkg.id)}
                                    onCheckedChange={() => handlePackageSelect(pkg.id)}
                                    className="bg-white border-gray-300"
                                  />
                                </div>
                              </td>
                              <td style={{ padding: '16px 20px', fontSize: '14px', color: '#1f2937', fontWeight: 500 }}>
                                {pkg.package_name}
                              </td>
                              <td style={{ padding: '16px 20px', fontSize: '14px', color: '#6b7280' }}>
                                {pkg.package_content}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* 联合体选项 - 只有当 allow_joint_bid 不为 0 时才显示 */}
                {detail.allow_joint_bid !== 0 && (
                  <div style={{
                    padding: '20px 24px',
                    backgroundColor: 'white',
                    borderRadius: '12px',
                    border: '1px solid #e5e7eb'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '12px' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <Checkbox
                            checked={isConsortium}
                            onCheckedChange={(checked: boolean | "indeterminate") => setIsConsortium(checked === true)}
                            className="bg-white border-gray-300"
                          />
                        </div>
                        <span style={{ fontSize: '14px', color: '#374151', fontWeight: 500 }}>
                          是否为联合体参加本次采购活动
                        </span>
                      </label>
                    </div>
                    {isConsortium && (
                      <Input
                        placeholder="请输入联合体成员名称（多个成员用逗号分隔）"
                        value={consortiumMembers}
                        onChange={(e) => setConsortiumMembers(e.target.value)}
                        style={{ marginTop: '12px', backgroundColor: 'white', border: '1px solid #d1d5db' }}
                      />
                    )}
                    <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '12px' }}>
                      如供应商以联合体形式参与本次采购活动，则由牵头单位注册报名，如有多名联合体成员单位，可将所有单位填入框内。
                    </p>
                  </div>
                )}
                  </>
                )}
              </div>

              {/* 弹窗底部 */}
              <div style={{
                padding: isMobile ? '16px' : '20px 32px',
                borderTop: '1px solid #e5e7eb',
                display: 'flex',
                flexDirection: isMobile ? 'column' : 'row',
                alignItems: isMobile ? 'stretch' : 'center',
                justifyContent: 'space-between',
                gap: isMobile ? '12px' : undefined,
                backgroundColor: '#fafbfc'
              }}>
                {detail.announcement_type === 4 ? (
                  <>
                    <div style={{ fontSize: isMobile ? '13px' : '14px', color: '#6b7280', lineHeight: 1.5, wordBreak: 'break-all' }}>
                      {otherAnnouncementFile ? (
                        <span>已选择文件：<span style={{ color: typeConfig.color, fontWeight: 600 }}>{otherAnnouncementFile.name}</span></span>
                      ) : (
                        <span style={{ color: '#ff4d4f' }}>请上传报名文件</span>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: '12px', width: isMobile ? '100%' : undefined, flexDirection: isMobile ? 'column' : 'row' }}>
                      <button
                        onClick={() => {
                          setShowRegisterModal(false);
                          setOtherAnnouncementFile(null);
                        }}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          padding: '12px 32px',
                          width: isMobile ? '100%' : undefined,
                          backgroundColor: 'white',
                          color: '#6b7280',
                          fontSize: '15px',
                          fontWeight: 600,
                          border: '1px solid #e5e7eb',
                          borderRadius: '10px',
                          cursor: 'pointer',
                          transition: 'all 0.2s'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = '#f9fafb';
                          e.currentTarget.style.borderColor = '#d1d5db';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'white';
                          e.currentTarget.style.borderColor = '#e5e7eb';
                        }}
                      >
                        取消
                      </button>
                      <button
                        onClick={handleOtherAnnouncementSubmit}
                        disabled={otherAnnouncementUploading || !otherAnnouncementFile}
                        onMouseEnter={(e) => {
                          if (!otherAnnouncementUploading && otherAnnouncementFile) {
                            e.currentTarget.style.transform = 'translateY(-2px)';
                            e.currentTarget.style.boxShadow = `0 8px 25px ${typeConfig.color}50`;
                          }
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = 'translateY(0)';
                          e.currentTarget.style.boxShadow = `0 4px 15px ${typeConfig.color}40`;
                        }}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          padding: '12px 32px',
                          width: isMobile ? '100%' : undefined,
                          backgroundColor: (otherAnnouncementUploading || !otherAnnouncementFile) ? '#d1d5db' : typeConfig.color,
                          color: 'white',
                          fontSize: '15px',
                          fontWeight: 600,
                          border: 'none',
                          borderRadius: '10px',
                          cursor: (otherAnnouncementUploading || !otherAnnouncementFile) ? 'not-allowed' : 'pointer',
                          boxShadow: `0 4px 15px ${typeConfig.color}40`,
                          transition: 'all 0.3s ease'
                        }}
                      >
                        {otherAnnouncementUploading ? '上传中...' : '上传报名文件'}
                        {!otherAnnouncementUploading && <ArrowRight style={{ width: '18px', height: '18px' }} />}
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <div style={{ fontSize: isMobile ? '13px' : '14px', color: '#6b7280', lineHeight: 1.5 }}>
                      已选择 <span style={{ color: typeConfig.color, fontWeight: 600 }}>{selectedPackages.length}</span> 个包件
                      {detail.billing_method === '按包收费' && (
                        <span>（按包收费：{formatMoney(detail.document_price)}/包）</span>
                      )}
                      ，应付金额：<span style={{ color: '#f59e0b', fontWeight: 700, fontSize: '18px' }}>{formatMoney(calculateAmount())}</span>
                    </div>
                    <div style={{ display: 'flex', gap: '12px', width: isMobile ? '100%' : undefined, flexDirection: isMobile ? 'column' : 'row' }}>
                      <button
                        onClick={() => setShowRegisterModal(false)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          padding: '12px 32px',
                          width: isMobile ? '100%' : undefined,
                          backgroundColor: 'white',
                          color: '#6b7280',
                          fontSize: '15px',
                          fontWeight: 600,
                          border: '1px solid #e5e7eb',
                          borderRadius: '10px',
                          cursor: 'pointer',
                          transition: 'all 0.2s'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = '#f9fafb';
                          e.currentTarget.style.borderColor = '#d1d5db';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'white';
                          e.currentTarget.style.borderColor = '#e5e7eb';
                        }}
                      >
                        取消
                      </button>
                      <button
                        onClick={handlePurchase}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = 'translateY(-2px)';
                          e.currentTarget.style.boxShadow = `0 8px 25px ${typeConfig.color}50`;
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = 'translateY(0)';
                          e.currentTarget.style.boxShadow = `0 4px 15px ${typeConfig.color}40`;
                        }}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          padding: '12px 32px',
                          width: isMobile ? '100%' : undefined,
                          backgroundColor: typeConfig.color,
                          color: 'white',
                          fontSize: '15px',
                          fontWeight: 600,
                          border: 'none',
                          borderRadius: '10px',
                          cursor: 'pointer',
                          boxShadow: `0 4px 15px ${typeConfig.color}40`,
                          transition: 'all 0.3s ease'
                        }}
                      >
                        立即购买
                        <ArrowRight style={{ width: '18px', height: '18px' }} />
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </>
      )}

      {/* 登录提示弹窗 */}
      {showLoginModal && (
        <>
          <div style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            backdropFilter: 'blur(4px)',
            zIndex: 50
          }} />
          <div style={{
            position: 'fixed',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 50,
            padding: '16px'
          }}>
            <div style={{
              backgroundColor: 'white',
              borderRadius: '20px',
              boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
              maxWidth: '400px',
              width: '100%',
              padding: '40px',
              textAlign: 'center'
            }}>
              <div style={{
                width: '80px',
                height: '80px',
                margin: '0 auto 24px',
                backgroundColor: '#fef3c7',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <AlertCircle style={{ width: '40px', height: '40px', color: '#f59e0b' }} />
              </div>
              <h3 style={{ fontSize: '20px', fontWeight: 700, color: '#1f2937', marginBottom: '12px' }}>温馨提示</h3>
              <p style={{ color: '#6b7280', marginBottom: '32px', lineHeight: 1.6 }}>
                请登录供应商账号后，再参加报名！
              </p>
              <div style={{ display: 'flex', gap: '12px' }}>
                <button
                  onClick={() => setShowLoginModal(false)}
                  style={{
                    flex: 1,
                    padding: '14px',
                    backgroundColor: '#f3f4f6',
                    color: '#6b7280',
                    fontSize: '16px',
                    fontWeight: 600,
                    border: 'none',
                    borderRadius: '12px',
                    cursor: 'pointer'
                  }}
                >
                  取消
                </button>
                <button
                  onClick={handleGoToLogin}
                  style={{
                    flex: 1,
                    padding: '14px',
                    backgroundColor: typeConfig.color,
                    color: 'white',
                    fontSize: '16px',
                    fontWeight: 600,
                    border: 'none',
                    borderRadius: '12px',
                    cursor: 'pointer'
                  }}
                >
                  登录
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* 历史变更公告清单弹窗 */}
      {showChangeHistoryModal && (
        <>
          <div
            style={{
              position: 'fixed',
              inset: 0,
              backgroundColor: 'rgba(0,0,0,0.6)',
              backdropFilter: 'blur(4px)',
              zIndex: 50
            }}
            onClick={() => setShowChangeHistoryModal(false)}
          />
          <div style={{
            position: 'fixed',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 50,
            padding: '16px',
            overflow: 'auto'
          }}>
            <div
              style={{
                backgroundColor: 'white',
                borderRadius: '20px',
                boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
                width: '80vw',
                maxWidth: '900px',
                maxHeight: '80vh',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* 弹窗头部 */}
              <div style={{
                padding: '24px 32px',
                borderBottom: '1px solid #e5e7eb',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}>
                <h3 style={{ fontSize: '20px', fontWeight: 600, color: '#1f2937' }}>历史变更公告</h3>
                <button
                  onClick={() => setShowChangeHistoryModal(false)}
                  style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '8px',
                    border: 'none',
                    backgroundColor: '#f3f4f6',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#6b7280'
                  }}
                >
                  <X style={{ width: '18px', height: '18px' }} />
                </button>
              </div>

              {/* 弹窗内容 */}
              <div style={{ padding: '24px 32px', overflowY: 'auto', flex: 1 }}>
                {loadingChangeHistory ? (
                  <div style={{ textAlign: 'center', padding: '40px' }}>
                    <p style={{ color: '#6b7280' }}>加载中...</p>
                  </div>
                ) : changeHistory.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '40px' }}>
                    <p style={{ color: '#6b7280' }}>暂无历史变更公告</p>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {changeHistory.map((item) => (
                      <div
                        key={item.id}
                        style={{
                          padding: '20px',
                          backgroundColor: '#f8fafc',
                          borderRadius: '12px',
                          border: '1px solid #e5e7eb',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between'
                        }}
                      >
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: '16px', fontWeight: 600, color: '#1f2937', marginBottom: '8px' }}>
                            {item.project_name}
                          </div>
                          <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '4px' }}>
                            项目编号：{item.project_no}
                          </div>
                          <div style={{ fontSize: '14px', color: '#6b7280' }}>
                            发布时间：{formatDate(item.publish_time)}
                          </div>
                        </div>
                        <button
                          onClick={() => {
                            setShowChangeHistoryModal(false);
                            navigate(`/announcement-detail/${item.id}`);
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#d46b08';
                            e.currentTarget.style.transform = 'translateY(-2px)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = '#f59e0b';
                            e.currentTarget.style.transform = 'translateY(0)';
                          }}
                          style={{
                            padding: '10px 24px',
                            backgroundColor: '#f59e0b',
                            color: 'white',
                            fontSize: '14px',
                            fontWeight: 600,
                            border: 'none',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            whiteSpace: 'nowrap',
                            transition: 'all 0.2s'
                          }}
                        >
                          点击前往
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}
      <Footer />
    </div>
  );
}
